import React from 'react';
import './HomeHero.css';

const FEATURES = [
  { icon: '🧹', title: 'Clean your monitor screen', desc: 'Makes it easy to spot dust, smudges, and fingerprints on any display or monitor.' },
  { icon: '🎯', title: 'Focus mode', desc: 'Reduce distractions and boost productivity with a clear white background.' },
  { icon: '💡', title: 'Zoom lighting', desc: 'Brighten your face evenly for video calls and recordings.' },
  { icon: '🔍', title: 'Monitor pixel test', desc: 'Detect stuck or dead pixels on your monitor display instantly.' },
  { icon: '📦', title: 'Digital lightbox tool', desc: 'Perfect white screen lightbox replacement for artists and designers.' },
  { icon: '⛶', title: 'True fullscreen white online', desc: 'Instant fullscreen white screen online — no apps needed. Perfect extensions-free on any display size.' },
  { icon: '⬇', title: 'Download up to 8K screen images', desc: 'Generate and download high-resolution white screen images up to 8K quality for professional use.' },
  { icon: '🌐', title: 'Universal compatibility', desc: 'Works on any device — no downloads or installations required.' },
];

const STEPS = [
  { icon: '🌐', title: 'Visit Website', desc: 'Open the tool on any browser on any device — no downloads needed.' },
  { icon: '🎨', title: 'Choose Color', desc: 'Select from multiple color presets on your specific needs.' },
  { icon: '⛶', title: 'Go Fullscreen', desc: 'Click fullscreen button or press F11 to maximize your workspace.' },
  { icon: '▶', title: 'Start Creating', desc: 'Your display is now ready for drawing, testing, focusing, or any creative work.' },
];

const STATS = [
  { val: '2M+', label: 'Active users worldwide', sub: 'Monthly Users' },
  { val: '195+', label: 'Global reach and accessibility', sub: 'Countries Served' },
  { val: '50K+', label: 'Daily creative sessions', sub: 'Sessions Daily' },
];

const REVIEWS = [
  { name: 'Sarah Chen', role: 'Digital Artist', stars: 5, text: 'This white screen tool has revolutionized my digital art workflow. I use it daily for tracing sketches and it works perfectly on my tablet. The fact that it\'s free and works instantly in the browser is incredible!' },
  { name: 'Marcus Rodriguez', role: 'IT Professional', stars: 5, text: 'As someone who tests monitors regularly, this white screen tool is essential for dead pixel detection and calibration. It loads faster than any desktop app and the Fullscreen mode is perfect for professional use.' },
  { name: 'Emily Johnson', role: 'Student', stars: 5, text: 'I discovered this white screen for studying — it eliminates distractions so I can focus. I use it for references on my laptop and even the black screen is great for a clutter-free environment.' },
];

const FAQS = [
  { q: 'What is this white screen tool used for?', a: 'Our white screen tool serves multiple purposes: digital lightbox for artists and animators, monitor cleaning and dead pixel detection, presentation backdrop, focus and productivity distraction-free environment, and a lighting source for webcam or photography.' },
  { q: 'How do I use the white screen as a digital lightbox?', a: 'Place paper on your screen and activate the white screen to create a digital lightbox. Adjust your monitor brightness for optimal tracing visibility. Perfect lightbox replacement for searching, animators, and anyone that requires backlighting.' },
  { q: 'Can I download white screen images in high resolution?', a: 'Yes, our white screen online lets you download white screen images up to 8K resolution (7680×4320) for professional printing, presentations, or high-quality projects. All downloads are completely free.' },
  { q: 'Is this white screen tool really free to use?', a: 'Yes, completely! Our white screen tool is completely free with no hidden costs, subscriptions, or ads. Accessible to everyone — artists, students, professionals, and anyone needing a reliable monitor solution.' },
  { q: 'Does the white screen work on all devices and monitors?', a: 'Yes! Our web-based white screen tool works instantly on any device including tablets, smartphones, laptops and desktops. It supports all monitor types and screen sizes. No downloads or installations required.' },
  { q: 'Can the white screen help with monitor testing and cleaning?', a: 'Absolutely! The white screen reveals dead pixels (black dots), dust, smudges, and screen imperfections easily. Our black screen can be used for comprehensive display and monitor testing.' },
  { q: 'Can I run the white screen in fullscreen mode?', a: 'Absolutely! Our white screen provides a true fullscreen experience for monitor calibration, or creating the ultimate focus environment. Press F11 or double-click to enter fullscreen mode.' },
];

export default function HomeHero({ children }) {
  return (
    <>
      {/* Features */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <h2>Why Use Our White Screen Tool?</h2>
            <p>Discover the practical benefits of this simple yet powerful white screen solution</p>
          </div>
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="feature-card">
                <span className="feat-icon">{f.icon}</span>
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-head">
            <h2>How It Works</h2>
            <p>Get started with our simple 4-step process</p>
          </div>
          <div className="steps-grid">
            {STEPS.map((s, i) => (
              <div key={i} className="step-card">
                <div className="step-num">{i + 1}</div>
                <div className="step-icon">{s.icon}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section stats-section">
        <div className="container">
          <div className="trusted-badge">✓ Trusted Worldwide</div>
          <h2>Loved by Users Globally</h2>
          <p>Join millions who trust our white screen tool for monitor testing, digital lighting, and creative projects worldwide</p>
          <div className="stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className="stat-card">
                <div className="stat-val">{s.val}</div>
                <div className="stat-sub">{s.sub}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="user-review-badge">⭐ User Reviews</div>
            <h2>What Our Users Say</h2>
            <p>Hear from artists, students, and professionals who use our white screen tool daily</p>
          </div>
          <div className="reviews-grid">
            {REVIEWS.map((r, i) => (
              <div key={i} className="review-card">
                <div className="review-top">
                  <div className="reviewer-avatar">{r.name[0]}</div>
                  <div>
                    <div className="reviewer-name">{r.name}</div>
                    <div className="reviewer-role">{r.role}</div>
                  </div>
                  <div className="review-stars">{'★'.repeat(r.stars)}</div>
                </div>
                <p className="review-text">"{r.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-alt" id="faq">
        <div className="container">
          <div className="section-head">
            <h2>Frequently Asked Questions</h2>
            <p>Have another question? Contact us or <a href="/contact">Discord</a>.</p>
          </div>
          <div className="faq-grid">
            {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>
    </>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-q">
        <span className="faq-arrow">{open ? '▼' : '▶'}</span>
        <span>{q}</span>
      </div>
      {open && <div className="faq-a">{a}</div>}
    </div>
  );
}
